import {
  differenceInCalendarDays,
  endOfMonth,
  format,
  getQuarter,
  isValid,
  parseISO,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
} from "date-fns"

export type TKlaraPreset = "7d" | "30d" | "90d" | "lifetime"

export type TKlaraPeriod = {kind: "preset"; preset: TKlaraPreset} | {kind: "custom"; from: string; to: string}

export const KLARA_PRESETS: readonly TKlaraPreset[] = ["7d", "30d", "90d", "lifetime"]

export const DEFAULT_KLARA_PERIOD: TKlaraPeriod = {kind: "preset", preset: "30d"}

export const MAX_CUSTOM_RANGE_DAYS = 400

const PERIOD_DAYS: Record<Exclude<TKlaraPreset, "lifetime">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
}

const DATE_FORMAT = "yyyy-MM-dd"
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const formatDay = (date: Date) => format(date, DATE_FORMAT)

// A strict YYYY-MM-DD that names a real day, so "2026-02-31" doesn't roll over into March.
const isDay = (raw: string) => {
  if (!DATE_PATTERN.test(raw)) {
    return false
  }
  const date = parseISO(raw)
  return isValid(date) && formatDay(date) === raw
}

// The number of days in a range, counting both ends.
export function customRangeDays(from: string, to: string): number {
  return differenceInCalendarDays(parseISO(to), parseISO(from)) + 1
}

/**
 * A custom range is valid when both ends are real days, it runs forwards, ends
 * no later than today and spans at most MAX_CUSTOM_RANGE_DAYS. The API doesn't
 * check any of this, so a bad range would silently return no data.
 */
export function validateCustomRange(from: string, to: string, today: Date = new Date()): boolean {
  return (
    isDay(from) &&
    isDay(to) &&
    from <= to &&
    to <= formatDay(today) &&
    customRangeDays(from, to) <= MAX_CUSTOM_RANGE_DAYS
  )
}

const isPreset = (raw: string | null): raw is TKlaraPreset => KLARA_PRESETS.includes(raw as TKlaraPreset)

/**
 * Read the period from the URL: a valid `from`/`to` pair wins over `period`,
 * an unknown or missing `period` falls back to the default.
 */
export function parseKlaraPeriod(params: URLSearchParams, today: Date = new Date()): TKlaraPeriod {
  const from = params.get("from")
  const to = params.get("to")
  if (from !== null && to !== null && validateCustomRange(from, to, today)) {
    return {kind: "custom", from, to}
  }
  const preset = params.get("period")
  return isPreset(preset) ? {kind: "preset", preset} : DEFAULT_KLARA_PERIOD
}

/** A `{from, to}` window (YYYY-MM-DD, inclusive); both bounds are omitted for the lifetime. */
export type TKlaraRange = {from?: string; to?: string}

/**
 * Map a period to the `{from, to}` window sent to the dashboard endpoints.
 * Presets roll back from today; "lifetime" omits both bounds so the API
 * returns all-time statistics; a custom range is sent as is.
 */
export function getPeriodRange(period: TKlaraPeriod, today: Date = new Date()): TKlaraRange {
  if (period.kind === "custom") {
    return {from: period.from, to: period.to}
  }
  if (period.preset === "lifetime") {
    return {}
  }
  return {
    from: formatDay(subDays(today, PERIOD_DAYS[period.preset] - 1)),
    to: formatDay(today),
  }
}

/**
 * The window of the same length ending the day before `range` starts, to
 * compare a period against. The lifetime has nothing before it.
 */
export function getPreviousRange(range: TKlaraRange): Required<TKlaraRange> | null {
  if (!range.from || !range.to) {
    return null
  }
  const start = parseISO(range.from)
  const days = customRangeDays(range.from, range.to)
  return {from: formatDay(subDays(start, days)), to: formatDay(subDays(start, 1))}
}

/**
 * Clamp a day into `range` (YYYY-MM-DD, inclusive); an open upper bound ends
 * on `today` (YYYY-MM-DD), an open lower bound does not limit it.
 */
export function clampDayToRange(day: string, range: TKlaraRange, today: string): string {
  const max = range.to ?? today
  if (day > max) {
    return max
  }
  if (range.from !== undefined && day < range.from) {
    return range.from
  }
  return day
}

export function isLifetime(period: TKlaraPeriod): boolean {
  return period.kind === "preset" && period.preset === "lifetime"
}

export function periodEndsToday(period: TKlaraPeriod, today: Date = new Date()): boolean {
  return period.kind === "preset" || period.to === formatDay(today)
}

export type TQuickRange = {
  key: string
  label: {kind: "month"; date: string} | {kind: "quarter"; quarter: number; year: number}
  from: string
  to: string
}

const monthRange = (start: Date, end: Date): TQuickRange => ({
  key: `month-${format(start, "yyyy-MM")}`,
  label: {kind: "month", date: formatDay(start)},
  from: formatDay(start),
  to: formatDay(end),
})

/**
 * Ready-made custom ranges: this month so far, the two full months before it
 * and this quarter so far.
 */
export function getQuickRanges(today: Date = new Date()): TQuickRange[] {
  const thisMonth = startOfMonth(today)
  const previousMonths = [1, 2].map(months => {
    const start = subMonths(thisMonth, months)
    return monthRange(start, endOfMonth(start))
  })
  const quarter = getQuarter(today)
  const year = today.getFullYear()

  return [
    monthRange(thisMonth, today),
    ...previousMonths,
    {
      key: `quarter-${year}-${quarter}`,
      label: {kind: "quarter", quarter, year},
      from: formatDay(startOfQuarter(today)),
      to: formatDay(today),
    },
  ]
}
